import isNumber from 'is-number';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left-3.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right-3.svg';

import { ReactComponent as SlidersIcon } from 'assets/icons/sliders.svg';
import { ReactComponent as SaveIcon } from 'assets/icons/save.svg';

import http from 'utils/http';

import { openAlert } from 'components/Alert/utils';
import InfoTooltip from 'components/InfoTooltip';
import Text from 'components/Text';

import Box from '../../../styles/Box';
import NumberInput from '../../../components/TopDonators/styles/NumberInput';
import Button from '../../../../UserPanel/components/ComponentWrapper/styles/Button';

const Wrapper = styled(Box)`
  border: 1px solid var(--text-color-light-gray);
  padding-top: 16px;
  padding-bottom: 16px;
  display: grid;
  grid-row-gap: 16px;
  grid-template-columns: 1fr;
`;

const SaveButton = styled(Button)`
  height: 37px;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 13px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  :disabled {
    background-color: var(--bg-switch);
  }
`;

const RowBase = styled.div`
  display: grid;
  column-gap: 13px;
  align-items: center;
  width: 100%;
  text-align: center;
`;

const HeadingRow = styled(RowBase)`
  white-space: nowrap;
  text-align: start;
  grid-template-columns: auto 1fr;
`;

const HeadingContent = styled.div`
  margin-left: 7px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const HeadingTitle = styled.div`
  font-weight: 800;

  button {
    margin-left: 8px;
  }
`;

const SmallText = styled.div`
  font-size: 12px;
`;

const Row = styled(RowBase)`
  column-gap: 10px;
  grid-template-columns: 1fr auto 1fr;

  svg {
    color: var(--border-color);
  }
`;

const AddTime = ({ extraTime }) => {
  const [minutes, setMinutes] = useState(0);
  const [money, setMoney] = useState(0);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setMinutes((money * extraTime) / 60);
  }, [money]);

  useEffect(() => {
    setMoney(minutes / (extraTime / 60));
  }, [minutes]);

  const onClick = async () => {
    setWorking(true);
    try {
      await http.post('/user/countdown/time', {
        value: minutes * 60,
      });
    } catch (e) {
      openAlert('error', 'Błąd zapisu', 'Ups, nie mogę dodać czasu, spróbuj ponownie później 🥲');
    } finally {
      setWorking(false);
    }
  };

  const isValid = () => isNumber(minutes) && minutes > 0;

  return (
    <Wrapper>
      <HeadingRow>
        <SlidersIcon />
        <HeadingContent>
          <HeadingTitle>
            Dodaj czas do odlicznika
            <InfoTooltip
              content={
                <>
                  <Text>
                    Za pomocą tej funkcji możesz w każdej chwili dodać dowolną ilość czasu do
                    swojego odliczania. Dodając czas w tym miejscu nie usuniesz doliczonego czasu
                    przez system za pozyskane wpłaty czy suby/bitsy z aktywności z Twitch lub Kick.
                  </Text>
                  <Text>Dodając minuty, dodasz wpisaną wartość do trwającego odliczania.</Text>
                  <Text>
                    Dodając kwotę, system ją przeliczy jakbyś dostał/a zwykłą wpłatę i doda
                    odpowiednią ilość czasu.
                  </Text>
                </>
              }
              placement="right"
              theme="dark"
            />
          </HeadingTitle>
          <SmallText>Dolicz tyle czasu ile tylko chcesz!</SmallText>
        </HeadingContent>
      </HeadingRow>

      <Row>
        <NumberInput
          defaultValue={minutes}
          name="minutes"
          prefix="+"
          suffix=" min"
          style={{ textAlign: 'center' }}
          onValueChange={({ floatValue }) => setMinutes(floatValue)}
          decimalScale={0}
          disabled={working}
          variant="tiny"
        />
        <ArrowLeftIcon />
        <SmallText>Dodaj minuty do odlicznika czasu</SmallText>
      </Row>

      <Row>
        <SmallText>
          <u style={{ fontWeight: 800 }}>lub</u>
          {' dodaj kwotę przeliczoną na czas'}
        </SmallText>
        <ArrowRightIcon />
        <NumberInput
          defaultValue={money}
          name="money"
          prefix="+"
          suffix=" PLN"
          style={{ textAlign: 'center' }}
          onValueChange={({ floatValue }) => setMoney(floatValue)}
          decimalScale={2}
          disabled={working}
          variant="tiny"
        />
      </Row>

      <SaveButton
        type="button"
        background="blue"
        onClick={onClick}
        loading={working}
        disabled={working || !isValid()}
      >
        <SaveIcon />
        Zapisz i dolicz czas
      </SaveButton>
    </Wrapper>
  );
};

AddTime.propTypes = {
  extraTime: PropTypes.number.isRequired,
};

export default AddTime;
